<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-4">
          <label class="label-input">Top Panel</label>
          <div class="group-item">
            <v-row class="mb-2">
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields.top.backgroundColor"
                  name="topBackgroundColor"
                  label="Background Color"
                  default-color="#0130E2"
                />
              </v-col>
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields.top.textColor"
                  name="topTextColor"
                  label="Text Color"
                  default-color="#FFFFFF"
                />
              </v-col>
            </v-row>
            <p class="mb-2 font-weight-bold">Title</p>
            <ckeditor :editor="editor" v-model="fields.top.title" :config="editorConfig" />
            <div class="my-4">
              <v-row>
                <v-col cols="12" md="6">
                  <DividerPrimary :divider="fields.dividerLeft" label="Left Divider" />
                </v-col>
                <v-col cols="12" md="6">
                  <DividerPrimary :divider="fields.dividerRight" label="Right Divider" />
                </v-col>
              </v-row>
            </div>
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Feature Items</label>
          <div class="group-item">
            <v-text-field
              v-model.number="fields.features.columns"
              label="Columns Per View"
              type="number"
              min="1"
              hide-details
              class="mb-4"
            />
            <div class="d-flex justify-end mb-2">
              <v-btn small text color="primary" @click="handleAddFeature">
                <v-icon size="16">mdi-plus</v-icon>
                <span>Add Feature</span>
              </v-btn>
            </div>
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.features.items" :key="`feature-item-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <div class="d-flex align-center" style="gap: 10px">
                      <img
                        :src="item.image.src"
                        alt=""
                        style="
                          width: 48px;
                          height: 48px;
                          object-fit: cover;
                          border: 1px solid #e0e0e0;
                          border-radius: 4px;
                        "
                      />
                      <div class="line-clamp-1" v-html="item.text.replace(/<[^>]*>/g, '').trim()"></div>
                    </div>
                    <v-btn icon @click.stop="handleRemoveFeature(index)">
                      <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <label class="mb-2 d-block">Text</label>
                  <ckeditor :editor="editor" v-model="item.text" :config="editorConfig" />
                  <p class="mb-2 mt-4 font-weight-bold">Image</p>
                  <UploadImage
                    @callback="obj => handleUploadFeatureImage(obj, index)"
                    :image="{ imageSrc: item.image.src }"
                    :height="`100`"
                    :typeUpload="'image'"
                    @callbackDelete="() => handleRemoveFeatureImage(index)"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Compare Benefits</label>
          <div class="group-item">
            <ColorPicker
              v-model="fields.compare.with.highlightBorderColor"
              name="compareWithBorderColor"
              label="Highlight Border Color"
              default-color="#0130E2"
              class="mb-4"
            />
            <v-row>
              <v-col cols="12" md="6">
                <p class="mb-2 font-weight-bold">Life Without (✘)</p>
                <ckeditor :editor="editor" v-model="fields.compare.without.content" :config="editorConfig" />
              </v-col>
              <v-col cols="12" md="6">
                <div>
                  <p class="mb-2 font-weight-bold">Life With (✔)</p>
                  <ckeditor :editor="editor" v-model="fields.compare.with.content" :config="editorConfig" />
                </div>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';
import DividerPrimary from './common/divider-primary.vue';
import ColorPicker from './common/color-picker.vue';
import { replaceImageUrl } from '../common/utils';

const defaultFeatureItem = () => ({
  text: '<p style="text-align:center;"><strong>FEATURE</strong></p>',
  image: {
    src: replaceImageUrl('https://cdn.skyfastimg.com/images/2026/06/04/image-6a2130d6808bb03837c3746f.png'),
  },
});

export default {
  name: 'fastvaluedeal-compare-benefits-section-editor',
  components: {
    UploadImage,
    SectionSettingsContainerFuild,
    DividerPrimary,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Fastvaluedeal Compare Benefits',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#F8F8F8',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        top: {
          backgroundColor: '#0130E2',
          textColor: '#FFFFFF',
          title: '<h2 style="text-align:center;"><strong>Enjoy Unlimited Channel Selection with WaveMax</strong></h2>',
        },
        dividerLeft: {
          show: true,
          maxWidth: 100,
          height: 2,
          backgroundColor: '#FFFFFF',
          align: 'right',
        },
        dividerRight: {
          show: true,
          maxWidth: 100,
          height: 2,
          backgroundColor: '#FFFFFF',
          align: 'left',
        },
        features: {
          columns: 6,
          items: [defaultFeatureItem(), defaultFeatureItem(), defaultFeatureItem()],
        },
        compare: {
          without: {
            content: '',
          },
          with: {
            content: '',
            highlightBorderColor: '#0130E2',
          },
        },
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        top: { ...this.fields.top, ...(extract.top || {}) },
        dividerLeft: { ...this.fields.dividerLeft, ...(extract.dividerLeft || {}) },
        dividerRight: { ...this.fields.dividerRight, ...(extract.dividerRight || {}) },
        features: {
          ...this.fields.features,
          ...(extract.features || {}),
          items: Array.isArray(extract.features?.items)
            ? extract.features.items.map(item => ({
                text: item.text || '',
                image: {
                  src: item.image?.src || '',
                },
              }))
            : this.fields.features.items,
        },
        compare: {
          without: { ...this.fields.compare.without, ...(extract.compare?.without || {}) },
          with: { ...this.fields.compare.with, ...(extract.compare?.with || {}) },
        },
      };
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const payload = {
        ...this.fields,
        features: {
          ...this.fields.features,
          items: (this.fields.features.items || []).map(item => ({
            text: item.text || '',
            image: {
              src: item.image?.src || '',
            },
          })),
        },
      };
      const updateHtml = updateHtmlFromData.sectionFastvaluedealCompareBenefits(
        this.sectionEditor.section.html,
        payload,
      );
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddFeature() {
      this.fields.features.items.push(defaultFeatureItem());
    },
    handleRemoveFeature(index) {
      this.fields.features.items.splice(index, 1);
    },
    handleUploadFeatureImage(obj, index) {
      if (!this.fields.features.items[index]) return;
      this.$set(this.fields.features.items[index].image, 'src', obj.imageSrc || '');
    },
    handleRemoveFeatureImage(index) {
      if (!this.fields.features.items[index]) return;
      this.$set(this.fields.features.items[index].image, 'src', '');
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
::v-deep .ck-editor__editable_inline {
  min-height: 120px !important;
}
</style>
